/**
 * Display-time layout helpers for the practitioner dashboard.
 *
 * These functions never persist. They only reshape the *visible* grid so
 * incomplete rows of equal-height widgets fill available width instead of
 * leaving holes when widgets are removed. Masonry stacks (a tall widget
 * beside a column of shorter ones) are left intact.
 */

export const DASHBOARD_GRID_COLS = 12;
export const DASHBOARD_ROW_HEIGHT = 88;
export const DASHBOARD_GRID_MARGIN: [number, number] = [16, 16];

export interface PackableLayoutItem {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

function rangesOverlap(a0: number, a1: number, b0: number, b1: number): boolean {
  return a0 < b1 && b0 < a1;
}

function distributeWidths(current: number[], targetTotal: number): number[] {
  const widths = [...current];
  let remaining = targetTotal - widths.reduce((sum, w) => sum + w, 0);
  while (remaining > 0) {
    for (let i = 0; i < widths.length && remaining > 0; i++) {
      widths[i] += 1;
      remaining -= 1;
    }
  }
  return widths;
}

/**
 * Stretch equal-height widgets that share a row so they fill the grid width.
 *
 * - A single compact widget (w ≤ 4) grows to half width, not full width.
 * - A lone full-width / already-wide widget is left alone.
 * - Rows that already fill 12 columns are unchanged.
 * - Rows that share vertical space with a differently sized neighbour
 *   (masonry) are unchanged, so a tall schedule beside a stack of cards
 *   keeps its shape.
 */
export function redistributeEqualRows<T extends PackableLayoutItem>(
  layout: readonly T[],
  cols = DASHBOARD_GRID_COLS,
): T[] {
  const items = layout.map((item) => ({ ...item }));

  const groups = new Map<string, T[]>();
  for (const item of items) {
    const key = `${item.y}:${item.h}`;
    const group = groups.get(key);
    if (group) group.push(item);
    else groups.set(key, [item]);
  }

  for (const group of groups.values()) {
    group.sort((a, b) => a.x - b.x);
    const y = group[0].y;
    const h = group[0].h;
    const occupied = new Set(group.map((item) => item.i));
    const blocked = items.some(
      (other) => !occupied.has(other.i) && rangesOverlap(other.y, other.y + other.h, y, y + h),
    );
    if (blocked) continue;

    const totalW = group.reduce((sum, item) => sum + item.w, 0);
    if (group.length === 1) {
      const only = group[0];
      if (only.w <= 4) {
        only.w = Math.min(6, cols);
        only.x = 0;
      }
      continue;
    }

    if (totalW >= cols) continue;

    const widths = distributeWidths(
      group.map((item) => item.w),
      cols,
    );
    let x = 0;
    group.forEach((item, index) => {
      item.w = widths[index];
      item.x = x;
      x += item.w;
    });
  }

  return items;
}

function packLeftToRight<T extends PackableLayoutItem>(items: readonly T[], cols: number): T[] {
  const result: T[] = [];
  let x = 0;
  let y = 0;
  let rowH = 0;

  for (const item of items) {
    const w = Math.min(Math.max(1, item.w), cols);
    if (x > 0 && x + w > cols) {
      y += rowH;
      x = 0;
      rowH = 0;
    }
    result.push({ ...item, x, y, w });
    x += w;
    rowH = Math.max(rowH, item.h);
    if (x >= cols) {
      y += rowH;
      x = 0;
      rowH = 0;
    }
  }

  return result;
}

/**
 * Scale a 12-column layout down for narrower viewports. Order is preserved
 * (y, then x). Narrow widths reflow left-to-right so spans cannot overflow.
 * Used only for display — editing stays on 12 columns.
 */
export function scaleLayoutForCols<T extends PackableLayoutItem>(
  layout: readonly T[],
  cols: number,
): T[] {
  if (cols >= DASHBOARD_GRID_COLS) return layout.map((item) => ({ ...item }));
  const sorted = [...layout].sort((a, b) => a.y - b.y || a.x - b.x);

  if (cols <= 1) {
    let y = 0;
    return sorted.map((item) => {
      const next = { ...item, x: 0, y, w: 1 };
      y += item.h;
      return next;
    });
  }

  const ratio = cols / DASHBOARD_GRID_COLS;
  const mapped = sorted.map((item) => ({
    ...item,
    w: Math.max(1, Math.round(item.w * ratio)),
  }));
  return packLeftToRight(mapped, cols);
}

export function displayColsForWidth(width: number): number {
  if (width > 0 && width < 760) return 1;
  if (width > 0 && width < 1024) return 6;
  return DASHBOARD_GRID_COLS;
}

export function layoutForDisplay<T extends PackableLayoutItem>(
  layout: readonly T[],
  cols: number,
): T[] {
  return redistributeEqualRows(scaleLayoutForCols(layout, cols), cols);
}
